import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { Attachment } from '../../types';
import { attachmentsApi, financeApi, tasksApi } from '../../services/api';
import { Button } from './Button';

interface AttachmentPanelProps {
  ownerId?: string;
  kind: 'task' | 'expense';
  disabledTitle: string;
  disabledText: string;
  emptyTitle: string;
  emptyText: string;
}

export function AttachmentPanel({
  ownerId,
  kind,
  disabledTitle,
  disabledText,
  emptyTitle,
  emptyText,
}: AttachmentPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadAttachments = useCallback(async () => {
    if (!ownerId) {
      setAttachments([]);
      return;
    }
    setLoading(true);
    try {
      const response = kind === 'task'
        ? await tasksApi.getAttachments(ownerId)
        : await financeApi.getExpenseAttachments(ownerId);
      setAttachments(response.data);
    } catch {
      setAttachments([]);
      toast.error('Falha ao carregar arquivos');
    } finally {
      setLoading(false);
    }
  }, [kind, ownerId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !ownerId) return;

    setUploading(true);
    try {
      const response = kind === 'task'
        ? await tasksApi.uploadAttachment(ownerId, file)
        : await financeApi.uploadExpenseAttachment(ownerId, file);
      setAttachments(prev => [response.data, ...prev]);
      toast.success('Arquivo anexado');
    } catch {
      toast.error('Falha ao anexar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    setBusyId(attachment.id);
    try {
      const response = await attachmentsApi.download(attachment.id);
      const responseContentType = response.headers['content-type'];
      const blob = new Blob([response.data], {
        type: attachment.mime_type || (typeof responseContentType === 'string' ? responseContentType : undefined) || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Falha ao baixar arquivo');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (attachment: Attachment) => {
    const confirmed = window.confirm(`Remover o arquivo "${attachment.filename}"?`);
    if (!confirmed) return;

    setBusyId(attachment.id);
    try {
      await attachmentsApi.delete(attachment.id);
      setAttachments(prev => prev.filter(item => item.id !== attachment.id));
      toast.success('Arquivo removido');
    } catch {
      toast.error('Falha ao remover arquivo');
    } finally {
      setBusyId(null);
    }
  };

  if (!ownerId) {
    return (
      <EmptyFiles
        icon={<Paperclip size={34} />}
        title={disabledTitle}
        text={disabledText}
      />
    );
  }

  return (
    <div className="p-5">
      <input ref={inputRef} type="file" className="hidden" onChange={handleUpload} />
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Paperclip size={16} className="text-gray-500" />
          {attachments.length} arquivo(s)
        </div>
        <Button
          type="button"
          size="sm"
          loading={uploading}
          icon={<Upload size={14} />}
          onClick={() => inputRef.current?.click()}
        >
          Anexar
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
        </div>
      ) : attachments.length === 0 ? (
        <EmptyFiles icon={<Paperclip size={34} />} title={emptyTitle} text={emptyText} compact />
      ) : (
        <div className="space-y-2">
          {attachments.map(attachment => (
            <div key={attachment.id} className="flex items-center gap-3 rounded border border-gray-100 p-3 hover:bg-gray-50">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded bg-primary-50 text-primary-600">
                <FileText size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-800">{attachment.filename}</p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(attachment.size)}{attachment.uploader_name ? ` por ${attachment.uploader_name}` : ''}
                </p>
              </div>
              <button
                type="button"
                disabled={busyId === attachment.id}
                onClick={() => handleDownload(attachment)}
                className="rounded p-1.5 text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-600 disabled:opacity-50"
                title="Baixar arquivo"
              >
                <Download size={16} />
              </button>
              <button
                type="button"
                disabled={busyId === attachment.id}
                onClick={() => handleDelete(attachment)}
                className="rounded p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                title="Remover arquivo"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatFileSize(size?: number | null): string {
  if (!size) return 'Tamanho nao informado';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function EmptyFiles({ icon, title, text, compact = false }: {
  icon: React.ReactNode;
  title: string;
  text: string;
  compact?: boolean;
}) {
  return (
    <div className={`text-center ${compact ? 'py-12' : 'py-20'} px-6`}>
      <div className="mb-4 flex justify-center text-primary-300">{icon}</div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">{text}</p>
    </div>
  );
}
